import { useState } from 'react';
import { Edit2, Plus, Trash2 } from 'react-feather';

import { deleteFile, uploadFile } from '@/shared/hooks/useS3';
import { useSecondaryAuthGuard } from '@/shared/hooks/useAuthGuard';
import apiToken, {
  handleApiError,
  isSuccess,
} from '@/shared/services/axiosInstance';
import Button from '@/shared/components/UI/Button';
import Input from '@/shared/components/UI/Input';
import FileInput from '@/shared/components/UI/FileInput';

import { AddressData } from '../types/AddressTypes';
import { MyPageProps } from '../types/MyPageTypes';
import AddAddressModal from './AddAddressModal';
import ChangePasswordModal from './ChangePasswordModal';

// ...existing imports...

const MyAccount = ({ userData }: MyPageProps) => {
  const [isEdit, setIsEdit] = useState(false);
  const [form, setForm] = useState({
    phoneNumber: userData.phoneNumber,
    companyName: userData.companyName,
    representativeName: userData.representativeName,
    businessRegistrationNumber: userData.businessRegistrationNumber,
    companyAddress: userData.companyAddress,
    businessCertificateUrl: userData.businessCertificateUrl,
    businessCertificateFile: null as File | null,
    postalCode: userData.postalCode,
    marketingAgree: userData.marketingAgree,
  });
  const [isChangePassword, setIsChangePassword] = useState(false);
  const [isAddAddress, setIsAddAddress] = useState(false);
  const [editingAddress, setEditingAddress] = useState<AddressData | undefined>(
    undefined,
  );

  const { isVerified } = useSecondaryAuthGuard();

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, files } = e.target;

    if (name !== 'businessCertificateFile') {
      setForm((prev) => ({ ...prev, [name]: value }));
      return;
    }

    const selectedFile = files?.[0];
    if (!selectedFile) {
      setForm((prev) => ({
        ...prev,
        businessCertificateFile: null,
      }));
      return;
    }

    const uploadedUrl = await uploadFile(selectedFile, 'BUSINESS_CERT');

    if (!uploadedUrl) {
      e.target.value = '';
      setForm((prev) => ({
        ...prev,
        businessCertificateFile: null,
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      businessCertificateFile: selectedFile,
      businessCertificateUrl: uploadedUrl,
    }));
  };

  const handleEdit = () => setIsEdit(true);

  const handleCancel = async () => {
    // 편집 중 새로 업로드했지만 저장하지 않은 임시 파일이 있다면 삭제
    if (
      form.businessCertificateUrl &&
      form.businessCertificateUrl.trim() !== '' &&
      form.businessCertificateUrl !== userData.businessCertificateUrl
    ) {
      const deleteSuccess = await deleteFile(form.businessCertificateUrl);
      if (!deleteSuccess) {
        console.error('Failed to delete the temporary file.');
      }
    }

    setIsEdit(false);
    setForm({
      phoneNumber: userData.phoneNumber,
      companyName: userData.companyName,
      representativeName: userData.representativeName,
      businessRegistrationNumber: userData.businessRegistrationNumber,
      companyAddress: userData.companyAddress,
      businessCertificateUrl: userData.businessCertificateUrl,
      businessCertificateFile: null,
      postalCode: userData.postalCode,
      marketingAgree: userData.marketingAgree,
    });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    const previousUrl = userData.businessCertificateUrl;

    try {
      const updateData = {
        companyName: form.companyName,
        companyAddress: form.companyAddress,
        representativeName: form.representativeName,
        phoneNumber: form.phoneNumber,
        businessRegistrationNumber: form.businessRegistrationNumber,
        businessCertificateUrl: form.businessCertificateUrl,
        postalCode: form.postalCode,
        marketingAgree: form.marketingAgree,
      };

      const response = await apiToken.patch('/api/v1/user', updateData);

      if (isSuccess(response.data)) {
        alert('Profile updated successfully!');

        // PATCH 성공한 이후에만 원본 파일 삭제
        if (
          previousUrl &&
          previousUrl.trim() !== '' &&
          previousUrl !== form.businessCertificateUrl
        ) {
          const deleteSuccess = await deleteFile(previousUrl);
          if (!deleteSuccess) {
            console.error(
              'Failed to delete the original file, but the update was successful.',
            );
          }
        }

        setIsEdit(false);
      }
    } catch (error) {
      const errorMessage = handleApiError(error);
      alert(errorMessage);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await apiToken.post('/api/v1/s3/download', {
        imageUrl: form.businessCertificateUrl,
      });

      if (isSuccess(response.data)) {
        const downloadUrl = response.data.data?.url;
        const fileName = response.data.data?.fileName || '사업자등록증.pdf';

        if (downloadUrl) {
          // Create a temporary link element for downloading
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = fileName;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    } catch (error) {
      const errorMessage = handleApiError(error);
      alert(errorMessage);
    }
  };
  // ...existing code...

  const deleteAccount = async () => {
    if (window.confirm('Are you sure you want to delete your account?')) {
      try {
        const response = await apiToken.delete('/api/v1/user');
        if (isSuccess(response.data)) {
          alert('Account deleted successfully.');
          sessionStorage.clear();
          window.location.href = '/';
        }
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to delete account:', errorMessage);
        alert(errorMessage);
      }
    }
  };

  const deleteAddress = async (addressUuid: string) => {
    if (window.confirm('Are you sure you want to delete this address?')) {
      try {
        const response = await apiToken.delete(
          `/api/v1/user/address/${addressUuid}`,
        );
        if (isSuccess(response.data)) {
          alert('Address deleted successfully.');
          window.location.reload();
        }
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to delete address:', errorMessage);
        alert(errorMessage);
      }
    }
  };
  return (
    <div>
      {isVerified && (
        <section className="mx-auto">
          <div className="w-full max-w-[48rem]">
            <h2 className="text-center text-[1.25rem] font-black text-[#95699f]">
              My Account
            </h2>
            <hr className="mx-auto my-6 mb-8 border border-[#ccc]" />
            <div className="mx-auto w-[720px] rounded-xl bg-white p-6 shadow-[7px_8px_14px_#22222208]">
              <div className="text-right">
                {!isEdit && (
                  <button
                    type="button"
                    className="cursor-pointer bg-transparent"
                    onClick={handleEdit}
                  >
                    <img src="/images/edit.svg" alt="edit" />
                  </button>
                )}
                {/* ) : (
                  <button
                    type="button"
                    className="cursor-pointer bg-transparent text-[#666]"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                )} */}
              </div>
              <form
                method="post"
                action="#"
                className="w-full"
                onSubmit={handleSave}
              >
                <div className="mb-5 flex items-center justify-between">
                  <label
                    htmlFor="email"
                    className="text-[14px] font-normal text-[#666]"
                  >
                    Email
                  </label>
                  <Input
                    type="text"
                    name="email"
                    value={userData.email}
                    readOnly
                    fullWidth={false}
                    className="mt-0 h-9 w-[360px] border border-[#ddd] bg-[#f3f3f3] px-5 text-[14px] text-[#666]"
                  />
                </div>
                <div className="mb-5 flex items-center justify-between">
                  <label
                    htmlFor="Phone nomber"
                    className="text-[14px] font-normal text-[#666]"
                  >
                    Phone number
                  </label>
                  <Input
                    type="tel"
                    name="phoneNumber"
                    value={form.phoneNumber}
                    onChange={handleChange}
                    readOnly={!isEdit}
                    fullWidth={false}
                    className="mt-0 h-9 w-[360px] px-5 text-[14px] text-[#222]"
                  />
                </div>
                <div className="mb-5 flex items-center justify-between">
                  <label
                    htmlFor="company-name"
                    className="text-[14px] font-normal text-[#666]"
                  >
                    Company Name
                  </label>
                  <Input
                    type="text"
                    name="companyName"
                    value={form.companyName}
                    onChange={handleChange}
                    readOnly={!isEdit}
                    fullWidth={false}
                    className="mt-0 h-9 w-[360px] px-5 text-[14px] text-[#222]"
                  />
                </div>
                <div className="mb-5 flex items-center justify-between">
                  <label
                    htmlFor="representative-name"
                    className="text-[14px] font-normal text-[#666]"
                  >
                    Representative Name
                  </label>
                  <Input
                    type="text"
                    name="representativeName"
                    value={form.representativeName}
                    onChange={handleChange}
                    readOnly={!isEdit}
                    fullWidth={false}
                    className="mt-0 h-9 w-[360px] px-5 text-[14px] text-[#222]"
                  />
                </div>
                <div className="mb-5 flex items-center justify-between">
                  <label
                    htmlFor="businessNumber"
                    className="text-[14px] font-normal text-[#666]"
                  >
                    Business registration number
                  </label>
                  <Input
                    type="text"
                    name="businessRegistrationNumber"
                    value={form.businessRegistrationNumber}
                    onChange={handleChange}
                    readOnly={!isEdit}
                    fullWidth={false}
                    className="mt-0 h-9 w-[360px] px-5 text-[14px] text-[#222]"
                  />
                </div>
                <div className="mb-5 flex items-center justify-between">
                  <label
                    htmlFor="address"
                    className="text-[14px] font-normal text-[#666]"
                  >
                    address
                  </label>
                  <Input
                    type="text"
                    name="companyAddress"
                    value={form.companyAddress}
                    onChange={handleChange}
                    readOnly={!isEdit}
                    fullWidth={false}
                    className="mt-0 h-9 w-[360px] px-5 text-[14px] text-[#222]"
                  />
                </div>
                <div className="mb-5 flex items-center justify-between">
                  <label
                    htmlFor="postalCode"
                    className="text-[14px] font-normal text-[#666]"
                  >
                    Postal Code
                  </label>
                  <Input
                    type="text"
                    name="postalCode"
                    value={form.postalCode}
                    onChange={handleChange}
                    readOnly={!isEdit}
                    fullWidth={false}
                    className="mt-0 h-9 w-[360px] px-5 text-[14px] text-[#222]"
                  />
                </div>
                <div className="mb-5 flex items-center justify-between">
                  <label
                    htmlFor="Certificate for Business Registration"
                    className="text-[14px] font-normal text-[#666]"
                  >
                    Certificate for Business Registration
                  </label>

                  {isEdit ? (
                    <FileInput
                      name="businessCertificateFile"
                      accept=".jpg,.jpeg,.png,.gif,.webp,.svg,.pdf,.mp4,.mov,.avi,.wmv,.flv,.webm"
                      onChange={handleChange}
                      buttonText="Upload"
                      selectedFileName={form.businessCertificateFile?.name}
                      fullWidth={false}
                      className="mt-1 h-9 w-[360px]"
                      buttonClassName="h-7 w-[85px] text-[13px]"
                    />
                  ) : (
                    <div className="relative mt-1 flex h-9 w-[360px] items-center rounded-full border border-[#ddd] pl-6 pr-28">
                      <p className="w-full overflow-hidden text-ellipsis whitespace-nowrap text-[14px] text-[#222]">
                        {(() => {
                          if (!form.businessCertificateUrl) return '';
                          const urlParts =
                            form.businessCertificateUrl.split('/');
                          const fileName =
                            urlParts[urlParts.length - 1]?.split('?')[0];
                          const fileNameOnly = fileName
                            ? fileName.split('.')[0]
                            : '';
                          if (fileNameOnly.length > 30) {
                            return `${fileNameOnly.substring(0, 30)}...`;
                          }
                          return `${fileNameOnly}`;
                        })()}
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleDownload}
                        className="absolute right-1 top-1/2 h-7 w-[85px] -translate-y-1/2 border text-[13px] font-medium hover:bg-[#b995be]"
                      >
                        Download
                      </Button>
                    </div>
                  )}
                </div>
                <div className="mb-5 flex items-center justify-between">
                  <label
                    htmlFor="marketingAgree"
                    className="text-[14px] font-normal text-[#666]"
                  >
                    Marketing Agreement
                  </label>
                  <input
                    type="checkbox"
                    name="marketingAgree"
                    checked={form.marketingAgree}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        marketingAgree: e.target.checked,
                      }))
                    }
                    className="h-5 w-5 cursor-pointer"
                    disabled={!isEdit}
                  />
                </div>
                {isEdit && (
                  <div className="settion-button mt-16 flex items-center justify-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="md"
                      onClick={handleCancel}
                      className="h-9 w-[136px] text-[14px]"
                    >
                      CANCEL
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      size="md"
                      className="h-9 w-[136px] text-[14px]"
                    >
                      SAVE
                    </Button>
                  </div>
                )}
              </form>
            </div>
            <div className="ml-8 mt-[60px]">
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-[1.125rem] font-bold">
                  Shipping Address list
                </h2>
                <button
                  type="button"
                  className="flex items-center gap-2 bg-transparent text-[#95699f]"
                  onClick={() => setIsAddAddress(true)}
                >
                  <Plus size={16} color="#95699f" strokeWidth={1.5} />
                  Add Address
                </button>
              </div>
              <div className="flex flex-col gap-4">
                {userData.addresses.length === 0 ? (
                  <p className="text-[16px] font-normal text-[#666]">
                    No saved addresses.
                  </p>
                ) : (
                  userData.addresses.map((address) => (
                    <div
                      key={address.addressUuid}
                      className="cursor-pointer rounded-lg bg-white p-6 shadow-[4px_8px_14px_#00000010]"
                    >
                      <div className="mb-4 flex items-center justify-between">
                        <h4 className="text-[1.125rem] font-semibold text-[#95699f]">
                          {address.isDefault ? 'Default' : 'Address'}
                        </h4>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="ml-3 cursor-pointer bg-transparent"
                            onClick={() => {
                              setEditingAddress(address);
                              setIsAddAddress(true);
                            }}
                          >
                            <Edit2 size={20} color="#666" strokeWidth={1.5} />
                          </button>
                          <button
                            type="button"
                            className="ml-3 cursor-pointer bg-transparent"
                            onClick={() => deleteAddress(address.addressUuid)}
                          >
                            <Trash2 size={20} color="#666" strokeWidth={1.5} />
                          </button>
                        </div>
                      </div>
                      <div className="flex gap-2 text-[16px] font-normal text-[#4b5563]">
                        <p>{address.addressDetail}</p>
                        <span>{address.postalCode}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="ml-8 mt-[60px]">
              <h2 className="mb-4 text-[1.125rem] font-bold">My info</h2>
              <ul>
                <li className="h-9 w-[156px] cursor-pointer text-[16px] font-normal">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsChangePassword(true)}
                    className="rounded-none px-0 font-normal text-[#666]"
                  >
                    Change password
                  </Button>
                </li>
                <li className="h-9 w-[156px] text-[16px]">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={deleteAccount}
                    className="rounded-none px-0 font-normal text-[#666]"
                  >
                    Delete account
                  </Button>
                </li>
              </ul>
            </div>
          </div>
        </section>
      )}
      {isChangePassword && (
        <ChangePasswordModal setIsChangePassword={setIsChangePassword} />
      )}
      {isAddAddress && (
        <AddAddressModal
          onClose={() => {
            setIsAddAddress(false);
            setEditingAddress(undefined);
          }}
          editAddress={editingAddress}
          onAddressAdded={() => {
            // Refresh user data after address change
            window.location.reload();
          }}
        />
      )}
    </div>
  );
};

export default MyAccount;
